import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { 
  Building2, 
  Mail, 
  Phone, 
  Star,
  StarOff,
  DollarSign,
  Handshake,
  FileText,
  TrendingUp,
  Briefcase,
} from "lucide-react";

interface Client {
  id: string;
  name: string;
  industry: string;
  contactPerson?: string;
  email: string;
  phone: string;
  status: 'Active' | 'Inactive' | 'Pending';
  totalDeals: number;
  totalRevenue: number;
  isFavorite: boolean;
  notes?: string;
}

interface ViewClientDialogProps {
  client: Client | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ViewClientDialog({
  client,
  open,
  onOpenChange,
}: ViewClientDialogProps) {
  if (!client) return null;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'Inactive':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'Pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Active':
        return '🟢';
      case 'Inactive':
        return '⚫';
      case 'Pending':
        return '🟡';
      default:
        return '⚫';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const DetailRow = ({ label, value, icon: Icon }: { label: string; value: string | number | React.ReactNode; icon?: React.ComponentType<{ className?: string }> }) => (
    <div className="flex items-center gap-3 py-2">
      {Icon && <Icon className="h-4 w-4 text-gray-400 flex-shrink-0" />}
      <div className="flex-1">
        <span className="text-sm font-medium text-gray-600">{label}:</span>
        <span className="ml-2 text-sm text-gray-900">{value}</span>
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[800px] h-[90vh] flex flex-col p-0 rounded-xl overflow-hidden">
        <DialogHeader className="px-6 pb-0 pt-6">
          <DialogTitle className="sr-only">
            Client Details - {client.name}
          </DialogTitle>
          <DialogDescription className="sr-only">
            View complete client information including contact details, business information, and performance metrics.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">
            {/* Client Header */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100 shadow-sm">
              <div className="flex items-center gap-6">
                <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Building2 className="h-8 w-8 text-blue-600" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h1 className="text-2xl font-bold text-gray-900">{client.name}</h1>
                    {client.isFavorite ? (
                      <Star className="h-5 w-5 text-yellow-400 fill-current" />
                    ) : (
                      <StarOff className="h-5 w-5 text-gray-400" />
                    )}
                  </div>
                  <div className="flex items-center gap-4">
                    <Badge className={getStatusColor(client.status)}>
                      <span className="mr-1">{getStatusIcon(client.status)}</span>
                      {client.status}
                    </Badge>
                    <span className="text-sm text-gray-600">{client.industry}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Mail className="h-5 w-5 text-blue-600" />
                Contact Information
              </h2>
              <div className="space-y-3">
                <DetailRow 
                  label="Email" 
                  value={client.email} 
                  icon={Mail}
                />
                <DetailRow 
                  label="Phone" 
                  value={client.phone} 
                  icon={Phone}
                />
              </div>
            </div>

            {/* Business Information */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Briefcase className="h-5 w-5 text-blue-600" />
                Business Information
              </h2>
              <div className="space-y-3">
                <DetailRow 
                  label="Industry" 
                  value={client.industry} 
                  icon={Building2}
                />
                <DetailRow 
                  label="Status" 
                  value={
                    <Badge className={getStatusColor(client.status)}>
                      <span className="mr-1">{getStatusIcon(client.status)}</span>
                      {client.status}
                    </Badge>
                  }
                />
              </div>
            </div>

            {/* Performance Metrics */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-blue-600" />
                Performance Metrics
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 border border-green-100">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <Handshake className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-green-600">Total Deals</p>
                      <p className="text-2xl font-bold text-green-700">{client.totalDeals}</p>
                    </div>
                  </div>
                </div>
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-100">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <DollarSign className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-blue-600">Total Revenue</p>
                      <p className="text-2xl font-bold text-blue-700">{formatCurrency(client.totalRevenue)}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Additional Notes */}
            {client.notes && (
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <FileText className="h-5 w-5 text-blue-600" />
                  Additional Notes
                </h2>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{client.notes}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
